import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BuildingOfficeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import LeadsService from '../services/leadsService';
import useFormFields from '../hooks/useFormFields';
import { validatePhoneNumber } from '../utils/phoneValidation';
import api from '../config/api';

const AutoCaptureForm = () => {
  const { fields, loading } = useFormFields();
  const [leadSources, setLeadSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    source: 'website',
    budget: '',
    notes: '',
    customFields: {}
  });
  const [phoneError, setPhoneError] = useState('');
  const [customFieldPhoneErrors, setCustomFieldPhoneErrors] = useState({});

  // Load lead sources
  useEffect(() => {
    loadLeadSources();
  }, []);

  const loadLeadSources = async () => {
    try {
      setLoadingSources(true);
      const response = await api.get('/lead-sources');
      if (response.data.success) {
        setLeadSources(response.data.sources || []);
      }
    } catch (error) {
      console.error('Error loading lead sources:', error);
    } finally {
      setLoadingSources(false);
    }
  };

  // Initialize dynamic field values and auto-populate from URL
  useEffect(() => {
    const initialData = {
      name: '',
      title: '',
      email: '',
      phone: '',
      source: 'website',
      budget: '',
      notes: '',
      customFields: {} // Initialize customFields object
    };

    // Add dynamic fields to customFields (excluding built-in fields)
    fields.forEach(field => {
      const builtInFields = ['name', 'email', 'phone', 'title', 'source', 'budget', 'notes', 'status', 'priority', 'value', 'company'];
      if (!builtInFields.includes(field.name)) {
        initialData.customFields[field.name] = '';
      }
    });

    // Auto-populate from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    
    if (sourceParam) {
      initialData.source = sourceParam;
    }

    setFormData(initialData);
  }, [fields]);

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Check if it's a built-in field or custom field
    const builtInFields = ['name', 'email', 'phone', 'title', 'source', 'budget', 'notes', 'status', 'priority', 'value', 'company'];
    const isBuiltIn = builtInFields.includes(name);
    
    // Check if it's a phone field (built-in or custom)
    const field = fields.find(f => f.name === name);
    const isPhoneField = name === 'phone' || (field && field.type === 'phone');
    
    if (isPhoneField) {
      const validation = validatePhoneNumber(value);
      if (value && !validation.isValid) {
        if (name === 'phone') {
          setPhoneError(validation.message);
        } else {
          setCustomFieldPhoneErrors({
            ...customFieldPhoneErrors,
            [name]: validation.message
          });
        }
      } else {
        if (name === 'phone') {
          setPhoneError('');
        } else {
          setCustomFieldPhoneErrors({
            ...customFieldPhoneErrors,
            [name]: ''
          });
        }
      }
      
      if (isBuiltIn) {
        setFormData(prev => ({
          ...prev,
          [name]: validation.formattedNumber || value
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          customFields: {
            ...prev.customFields,
            [name]: validation.formattedNumber || value
          }
        }));
      }
    } else {
      if (isBuiltIn) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          customFields: {
            ...prev.customFields,
            [name]: value
          }
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone numbers before submission
    if (formData.phone) {
      const phoneValidation = validatePhoneNumber(formData.phone);
      if (!phoneValidation.isValid) {
        setPhoneError(phoneValidation.message);
        return;
      }
    }
    
    // Validate custom phone fields
    let hasPhoneErrors = false;
    const newPhoneErrors = {};
    fields.forEach(field => {
      if (field.type === 'phone' && formData.customFields && formData.customFields[field.name]) {
        const validation = validatePhoneNumber(formData.customFields[field.name]);
        if (!validation.isValid) {
          newPhoneErrors[field.name] = validation.message;
          hasPhoneErrors = true;
        }
      }
    });
    
    if (hasPhoneErrors) {
      setCustomFieldPhoneErrors(newPhoneErrors);
      return;
    }
    
    setStatus('loading');

    try {
      // Get source from URL parameters or referrer
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source') || 'website';
      const campaign = urlParams.get('campaign') || '';
      
      // Validate required fields
      if (!formData.name) {
        setStatus('error');
        setMessage('Name is required.');
        return;
      }
      
      // Email or phone at least one should be provided
      if (!formData.email && !formData.phone) {
        setStatus('error');
        setMessage('Please provide either email or phone number.');
        return;
      }

      // Check if title field is required and validate only if it exists in fields
      const titleField = fields.find(field => field.name === 'title');
      if (titleField && titleField.required && !formData.title) {
        setStatus('error');
        setMessage('Please select your enquiry purpose.');
        return;
      }
      
      // Get the label for the selected title option
      const selectedOption = titleField?.options?.find(option => option.value === formData.title);
      const titleLabel = selectedOption ? selectedOption.label : formData.title;

      const leadData = {
        title: titleLabel || formData.title || 'New Lead',
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone?.trim() || '',
        source: source.toLowerCase(),
        budget: formData.budget || '',
        notes: (formData.notes || '') + (campaign ? ` (Campaign: ${campaign})` : ''),
        status: 'new',
        priority: 'medium',
        value: 0,
        customFields: formData.customFields || {} // Include custom fields
      };

      console.log('🔄 Submitting auto-capture lead with custom fields:', leadData.customFields);

      // Use the same LeadsService as Add Lead form
      const result = await LeadsService.createLead(leadData);

      if (result && result.success) {
        setStatus('success');
        setMessage('Thank you! Your quote request has been submitted successfully. Our team will contact you within 24 hours!');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          const resetData = {
            title: '',
            name: '',
            email: '',
            phone: '',
            source: 'website',
            budget: '',
            notes: '',
            customFields: {}
          };
          
          // Reset custom fields
          fields.forEach(field => {
            const builtInFields = ['name', 'email', 'phone', 'title', 'source', 'budget', 'notes', 'status', 'priority', 'value', 'company'];
            if (!builtInFields.includes(field.name)) {
              resetData.customFields[field.name] = '';
            }
          });
          
          setFormData(resetData);
          setStatus('idle');
          setMessage('');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(result?.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
      if (error.response?.data?.message) {
        setMessage(error.response.data.message);
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">{message}</p>
          <div className="animate-pulse">
            <div className="h-2 bg-green-200 rounded-full">
              <div className="h-2 bg-green-500 rounded-full w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Request a Quote
          </h1>
          <p className="text-gray-600">
            Tell us about your project and get a personalized solution
          </p>
        </div>

        {status === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            
            {/* Name - First */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                id="name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your full name"
              />
            </div>

            {/* Title/Purpose - Second (if not in custom fields) */}
            {!fields.find(f => f.name === 'title') && (
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title / Purpose
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.title || ''}
                  onChange={handleChange}
                  placeholder="e.g., Product Demo, Sales Inquiry"
                />
              </div>
            )}

            {/* Email - Third */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                id="email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
              />
            </div>

            {/* Phone - Fourth */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  phoneError ? 'border-red-500' : 'border-gray-300'
                }`}
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
              />
              {phoneError && (
                <p className="mt-1 text-sm text-red-600">{phoneError}</p>
              )}
            </div>

            {/* Budget Range - Fifth */}
            <div className="sm:col-span-2">
              <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                Budget Range
              </label>
              <input
                type="text"
                name="budget"
                id="budget"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.budget}
                onChange={handleChange}
                placeholder="e.g., ₹1,00,000 - ₹2,50,000 or ₹75,000"
              />
            </div>

            {/* Dynamic Custom Fields - After built-in fields */}
            {fields.filter(field => {
              const builtInFields = ['name', 'email', 'phone', 'title', 'source', 'budget', 'notes', 'status', 'priority', 'value', 'company'];
              return !builtInFields.includes(field.name);
            }).map((field) => (
              <div key={field._id} className={field.type === 'textarea' ? 'sm:col-span-2' : ''}>
                <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                
                {field.type === 'text' && (
                  <input
                    type="text"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.customFields?.[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}
                
                {field.type === 'email' && (
                  <input
                    type="email"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.customFields?.[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}
                
                {field.type === 'phone' && (
                  <div>
                    <input
                      type="tel"
                      name={field.name}
                      id={field.name}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        customFieldPhoneErrors[field.name] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      value={formData.customFields?.[field.name] || ''}
                      onChange={handleChange}
                      placeholder={field.placeholder || "Enter 10-digit mobile number"}
                      maxLength="10"
                      required={field.required}
                    />
                    {customFieldPhoneErrors[field.name] && (
                      <p className="mt-1 text-sm text-red-600">{customFieldPhoneErrors[field.name]}</p>
                    )}
                  </div>
                )}
                
                {field.type === 'number' && (
                  <input
                    type="number"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.customFields?.[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}
                
                {field.type === 'textarea' && (
                  <textarea
                    name={field.name}
                    id={field.name}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.customFields?.[field.name] || ''}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    required={field.required}
                  />
                )}
                
                {field.type === 'select' && (
                  <select
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.customFields?.[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                  >
                    <option value="">{field.placeholder || `Select ${field.label}...`}</option>
                    {field.options?.map((option, index) => (
                      <option key={index} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                )}
                
                {field.type === 'date' && (
                  <input
                    type="date"
                    name={field.name}
                    id={field.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.customFields?.[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                  />
                )}
              </div>
            ))}
            
            {/* Notes - Last */}
            <div className="sm:col-span-2">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                name="notes"
                id="notes"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Tell us more about your requirements..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="submit"
              disabled={status === 'loading'}
              className={`px-6 py-2 rounded-lg font-medium text-white transition-colors ${
                status === 'loading'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              }`}
            >
              {status === 'loading' ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                  Submitting...
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>We respect your privacy. Your information is secure and will not be shared.</p>
        </div>
      </div>
    </div>
  );
};

export default AutoCaptureForm;
